import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, MapPin, Clock, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { COMPANY, SERVICE_OPTIONS } from '../data/site';
import Reveal, { ChapterLabel } from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: '',
  phone: '',
  email: '',
  start_address: '',
  target_address: '',
  preferred_date: '',
  service_type: '',
  message: '',
};

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-brand focus:ring-4 focus:ring-brand/15';

const INFO = [
  { icon: Mail, label: 'E-Mail', value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: 'contact-info-email' },
  { icon: MapPin, label: 'Adresse', value: `${COMPANY.street}, ${COMPANY.city}`, testid: 'contact-info-address' },
  { icon: Clock, label: 'Erreichbar', value: COMPANY.hours, testid: 'contact-info-hours' },
];

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || 'Anfrage erfolgreich gesendet!');
      setSent(true);
      setForm(initialForm);
    } catch (err) {
      toast.error('Senden fehlgeschlagen. Bitte versuchen Sie es erneut oder schreiben Sie uns per E-Mail.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative overflow-hidden bg-navy py-24 md:py-32">
      <div className="fm-grid-bg pointer-events-none absolute inset-0 opacity-50" />
      <div className="fm-glow pointer-events-none absolute -left-40 bottom-0 h-[500px] w-[500px] opacity-70" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ChapterLabel index="06" title="Kontakt" dark />
        <div className="mt-8 grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Jetzt unverbindliches <span className="text-brand">Angebot anfragen</span>.
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-slate-300">
                Erzählen Sie uns kurz, worum es geht – wir melden uns innerhalb von
                24 Stunden mit einem fairen, transparenten Angebot bei Ihnen.
              </p>
            </Reveal>

            <div className="mt-10 space-y-4">
              {INFO.map(({ icon: Icon, label, value, href, testid }, i) => (
                <Reveal key={label} delay={0.15 + i * 0.08}>
                  {href ? (
                    <a
                      href={href}
                      data-testid={testid}
                      className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur transition-colors duration-300 hover:border-brand/50 hover:bg-white/[0.08]"
                    >
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand/15 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span>
                        <span className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-400">{label}</span>
                        <span className="block font-display text-base font-bold text-white">{value}</span>
                      </span>
                    </a>
                  ) : (
                    <div data-testid={testid} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand/15 text-brand">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span>
                        <span className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-400">{label}</span>
                        <span className="block font-display text-base font-bold text-white">{value}</span>
                      </span>
                    </div>
                  )}
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.2} className="lg:col-span-7">
            <div className="rounded-[2rem] bg-white p-6 shadow-2xl shadow-black/30 sm:p-10">
              {sent ? (
                <div className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="contact-success">
                  <span className="grid h-20 w-20 place-items-center rounded-full bg-brand/10">
                    <CheckCircle2 className="h-10 w-10 text-brand" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-extrabold text-navy">Vielen Dank!</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-500">
                    Ihre Anfrage ist bei uns eingegangen. Wir melden uns innerhalb von
                    24 Stunden bei Ihnen – versprochen.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contact-new-request-button"
                    className="mt-8 rounded-full border-2 border-navy px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white"
                  >
                    Weitere Anfrage stellen
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Name *</label>
                    <input id="cf-name" data-testid="contact-form-name-input" required minLength={2} value={form.name} onChange={update('name')} placeholder="Ihr Name" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Telefonnummer *</label>
                    <input id="cf-phone" data-testid="contact-form-phone-input" required type="tel" value={form.phone} onChange={update('phone')} placeholder="+49 ..." className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">E-Mail *</label>
                    <input id="cf-email" data-testid="contact-form-email-input" required type="email" value={form.email} onChange={update('email')} placeholder="ihre@email.de" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="cf-start" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Startadresse</label>
                    <input id="cf-start" data-testid="contact-form-start-address-input" value={form.start_address} onChange={update('start_address')} placeholder="Straße, PLZ Ort" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="cf-target" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Zieladresse</label>
                    <input id="cf-target" data-testid="contact-form-target-address-input" value={form.target_address} onChange={update('target_address')} placeholder="Straße, PLZ Ort" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="cf-date" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Gewünschter Termin</label>
                    <input id="cf-date" data-testid="contact-form-date-input" type="date" value={form.preferred_date} onChange={update('preferred_date')} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="cf-service" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Art der Leistung *</label>
                    <select id="cf-service" data-testid="contact-form-service-select" required value={form.service_type} onChange={update('service_type')} className={inputCls}>
                      <option value="" disabled>Bitte wählen</option>
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Nachricht *</label>
                    <textarea id="cf-message" data-testid="contact-form-message-input" required minLength={5} rows={4} value={form.message} onChange={update('message')} placeholder="Beschreiben Sie kurz Ihr Vorhaben ..." className={`${inputCls} resize-none`} />
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-form-submit-button"
                      className="group flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-base font-bold text-white shadow-xl shadow-brand/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {sending ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          Unverbindliches Angebot anfragen
                          <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                        </>
                      )}
                    </button>
                    <p className="mt-4 text-center text-xs text-slate-400">
                      Unverbindlich & kostenfrei. Ihre Daten werden vertraulich behandelt.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
